import React from "react";

/**
 * Manuscript-grade engraving ornaments.
 *
 * These are richer than simple icons — they're complete editorial scenes
 * with annotation lines, dimensional callouts, blueprint markings and
 * marginalia. Used as inter-chapter interludes ("breathing spreads") and
 * as cinematic plates inside the longer chapters.
 *
 * Strict palette: tobacco brown #4B2E1F on the pearl/ivory canvas.
 * No fills, no gradients — only line work of varying weights.
 */

const TOBACCO = "#4B2E1F";

/* ───────────────────────── BotanicalTobaccoStudy ─────────────────────────
 * A full botanical plate: tobacco leaf with annotated veins, root system,
 * 3 seed cross-sections, ruled measurement and marginalia.
 * ──────────────────────────────────────────────────────────────────────── */
export function BotanicalTobaccoStudy({ className = "" }) {
  return (
    <svg
      viewBox="0 0 800 520"
      fill="none"
      className={className}
      aria-hidden="true"
      data-testid="botanical-tobacco-study"
    >
      <g stroke={TOBACCO} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {/* ── Plate border + marginalia ────────────────────────────── */}
        <rect x="20" y="20" width="760" height="480" opacity="0.35" />
        <rect x="34" y="34" width="732" height="452" opacity="0.18" />

        {/* Folio mark — top left */}
        <text x="48" y="58" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.7" stroke="none">
          PLATE · BOTANICA
        </text>
        <text x="48" y="72" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.5" stroke="none">
          NICOTIANA TABACUM
        </text>

        {/* Folio mark — top right */}
        <text x="752" y="58" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.7" stroke="none" textAnchor="end">
          FOLIO · 014
        </text>
        <text x="752" y="72" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.5" stroke="none" textAnchor="end">
          MMXLVIII
        </text>

        {/* ── Central leaf — large, anatomically annotated ─────────── */}
        <g transform="translate(400 268)">
          {/* main leaf outline */}
          <path
            d="M0 -180 C -78 -148 -120 -88 -120 -10 C -120 78 -78 168 0 200 C 78 168 120 78 120 -10 C 120 -88 78 -148 0 -180 Z"
          />
          {/* mid vein */}
          <path d="M0 -176 L 0 196" strokeWidth="1.25" />
          {/* lateral veins — both sides, regular cadence */}
          {[-140, -100, -60, -20, 22, 64, 108, 150].map((y) => (
            <g key={y}>
              <path d={`M0 ${y} Q -50 ${y + 12} -94 ${y + 32}`} opacity="0.85" />
              <path d={`M0 ${y} Q 50 ${y + 12} 94 ${y + 32}`} opacity="0.85" />
              {/* micro-veins */}
              <path d={`M-46 ${y + 10} L -56 ${y + 18}`} opacity="0.45" />
              <path d={`M-72 ${y + 22} L -78 ${y + 32}`} opacity="0.45" />
              <path d={`M46 ${y + 10} L 56 ${y + 18}`} opacity="0.45" />
              <path d={`M72 ${y + 22} L 78 ${y + 32}`} opacity="0.45" />
            </g>
          ))}
          {/* serration ticks along the edge */}
          {Array.from({ length: 22 }).map((_, i) => {
            const t = i / 21;
            const y = -180 + t * 380;
            const xR = 120 * Math.sin(Math.PI * t) * 0.96;
            return (
              <g key={i}>
                <path d={`M${-xR} ${y} l -3 1`} opacity="0.55" />
                <path d={`M${xR} ${y} l 3 1`} opacity="0.55" />
              </g>
            );
          })}

          {/* annotation callouts */}
          <g opacity="0.7">
            <path d="M-94 -110 L -188 -148" />
            <circle cx="-94" cy="-110" r="2" fill={TOBACCO} stroke="none" />
            <text x="-198" y="-150" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="end">
              APEX · CAPA
            </text>

            <path d="M104 30 L 198 60" />
            <circle cx="104" cy="30" r="2" fill={TOBACCO} stroke="none" />
            <text x="208" y="62" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none">
              MID · LIGERO
            </text>

            <path d="M-86 124 L -188 162" />
            <circle cx="-86" cy="124" r="2" fill={TOBACCO} stroke="none" />
            <text x="-198" y="164" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="end">
              BASE · VOLADO
            </text>
          </g>

          {/* petiole + roots */}
          <path d="M0 200 L 0 232" strokeWidth="1.4" />
          <path d="M0 232 Q -22 250 -42 264" />
          <path d="M0 232 Q 22 250 42 264" />
          <path d="M0 232 Q -8 264 -16 278" />
          <path d="M0 232 Q 8 264 16 278" />
          <path d="M-32 256 L -48 272" opacity="0.6" />
          <path d="M32 256 L 48 272" opacity="0.6" />
        </g>

        {/* ── Seed cross-sections — bottom-left ────────────────────── */}
        <g className="ms-anim-breathe-x">
        <g transform="translate(98 432)" opacity="0.85">
          <circle cx="0" cy="0" r="14" />
          <circle cx="0" cy="0" r="9" opacity="0.55" />
          <path d="M-9 0 Q 0 -3 9 0" opacity="0.5" />
          <text x="0" y="34" fontFamily="Space Mono, monospace" fontSize="8" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">
            SEMEN · I
          </text>
        </g>
        <g transform="translate(150 432)" opacity="0.85">
          <circle cx="0" cy="0" r="12" />
          <circle cx="0" cy="0" r="7" opacity="0.55" />
          <text x="0" y="34" fontFamily="Space Mono, monospace" fontSize="8" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">
            SEMEN · II
          </text>
        </g>
        <g transform="translate(200 432)" opacity="0.85">
          <circle cx="0" cy="0" r="13" />
          <circle cx="0" cy="0" r="8" opacity="0.55" />
          <path d="M-8 -2 Q 0 -8 8 -2" opacity="0.5" />
          <text x="0" y="34" fontFamily="Space Mono, monospace" fontSize="8" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">
            SEMEN · III
          </text>
        </g>
        </g>

        {/* ── Measurement ruler — right margin ─────────────────────── */}
        <g transform="translate(680 120)" opacity="0.6">
          <path d="M0 0 L 0 280" strokeWidth="1.2" />
          {Array.from({ length: 15 }).map((_, i) => {
            const long = i % 5 === 0;
            return (
              <g key={i}>
                <path d={`M0 ${i * 20} l ${long ? 12 : 6} 0`} />
              </g>
            );
          })}
          <text x="22" y="6" fontFamily="Space Mono, monospace" fontSize="8" letterSpacing="2.2" fill={TOBACCO} stroke="none">CM · 0</text>
          <text x="22" y="106" fontFamily="Space Mono, monospace" fontSize="8" letterSpacing="2.2" fill={TOBACCO} stroke="none">CM · 25</text>
          <text x="22" y="206" fontFamily="Space Mono, monospace" fontSize="8" letterSpacing="2.2" fill={TOBACCO} stroke="none">CM · 50</text>
        </g>

        {/* Bottom-right marginalia */}
        <text x="752" y="468" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="13" fill={TOBACCO} opacity="0.7" stroke="none" textAnchor="end">
          Estudio botánico — Las Matas de Farfán
        </text>
      </g>
    </svg>
  );
}

/* ───────────────────────── BlueprintCigar ─────────────────────────
 * Annotated cigar manuscript composition with blueprint markings,
 * length/ring callouts, wrapper/binder/filler labels, and notation.
 * Sits as the interlude before Chapter IV (Master Blender).
 * ──────────────────────────────────────────────────────────────── */
export function BlueprintCigar({ className = "" }) {
  return (
    <svg
      viewBox="0 0 900 340"
      fill="none"
      className={className}
      aria-hidden="true"
      data-testid="blueprint-cigar"
    >
      <g stroke={TOBACCO} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {/* Plate border */}
        <rect x="16" y="16" width="868" height="308" opacity="0.3" />
        <rect x="28" y="28" width="844" height="284" opacity="0.16" />

        {/* Folio top */}
        <text x="44" y="50" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.7" stroke="none">
          PLATE · CIGARRO · BLUEPRINT
        </text>
        <text x="856" y="50" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.6" stroke="none" textAnchor="end">
          FOLIO · 022 · LENGTH 165MM · RING 52
        </text>

        {/* Cigar body — horizontal */}
        <g transform="translate(140 170)">
          {/* head (rounded) */}
          <path d="M0 -22 C -18 -22 -32 -10 -32 0 C -32 10 -18 22 0 22" />
          {/* main body */}
          <path d="M0 -22 L 540 -22" />
          <path d="M0 22 L 540 22" />
          {/* foot taper */}
          <path d="M540 -22 L 600 -10" />
          <path d="M540 22 L 600 10" />
          <path d="M600 -10 L 600 10" />

          {/* cap stitch line */}
          <path d="M-22 -16 Q -8 -8 -22 0 Q -8 8 -22 16" opacity="0.6" />

          {/* wrapper diagonal wraps — periodic */}
          {[40, 88, 136, 184, 232, 280, 328, 376, 424, 472, 520].map((x) => (
            <path key={x} d={`M${x} -22 L ${x + 32} 22`} opacity="0.35" />
          ))}

          {/* band */}
          <g className="ms-anim-breathe-x">
          <rect x="68" y="-24" width="72" height="48" opacity="0.85" />
          <rect x="72" y="-20" width="64" height="40" opacity="0.45" />
          <text x="104" y="6" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="14" fill={TOBACCO} stroke="none" textAnchor="middle">
            Ramírez
          </text>
          </g>

          {/* internal callouts */}
          <g opacity="0.7">
            {/* Wrapper */}
            <path d="M260 -22 L 260 -68" />
            <circle cx="260" cy="-22" r="2" fill={TOBACCO} stroke="none" />
            <text x="260" y="-76" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">
              WRAPPER · CAPA
            </text>
            <text x="260" y="-88" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="11" fill={TOBACCO} opacity="0.7" stroke="none" textAnchor="middle">
              Cedar · Cocoa · Leather
            </text>

            {/* Binder */}
            <path d="M360 22 L 360 104" />
            <circle cx="360" cy="22" r="2" fill={TOBACCO} stroke="none" />
            <text x="360" y="120" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">
              BINDER · CAPOTE
            </text>
            <text x="360" y="134" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="11" fill={TOBACCO} opacity="0.7" stroke="none" textAnchor="middle">
              Earth · Hay · Mineral
            </text>

            {/* Filler */}
            <path d="M460 -22 L 460 -68" />
            <circle cx="460" cy="-22" r="2" fill={TOBACCO} stroke="none" />
            <text x="460" y="-76" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">
              FILLER · TRIPA
            </text>
            <text x="460" y="-88" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="11" fill={TOBACCO} opacity="0.7" stroke="none" textAnchor="middle">
              Spice · Espresso · Almond
            </text>
          </g>

          {/* Length dimension below */}
          <g opacity="0.5">
            <path d="M-32 76 L 600 76" />
            <path d="M-32 72 L -32 80" />
            <path d="M600 72 L 600 80" />
            <text x="284" y="92" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none">
              165 MM · GRAN TORO
            </text>
          </g>
        </g>

        {/* Bottom-left signature */}
        <text x="44" y="304" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="12" fill={TOBACCO} opacity="0.7" stroke="none">
          Drawn by the master blender's hand.
        </text>
      </g>
    </svg>
  );
}

/* ───────────────────────── MapMeridian ─────────────────────────────
 * Map fragment showing Las Matas de Farfán nestled at the foot of the
 * Cordillera Central, with compass rose, latitude/longitude marginalia
 * and a delicate map graticule. Used as the interlude before The Land.
 * ──────────────────────────────────────────────────────────────────── */
export function MapMeridian({ className = "" }) {
  return (
    <svg
      viewBox="0 0 900 320"
      fill="none"
      className={className}
      aria-hidden="true"
      data-testid="map-meridian"
    >
      <g stroke={TOBACCO} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {/* Plate border */}
        <rect x="16" y="16" width="868" height="288" opacity="0.3" />
        <rect x="28" y="28" width="844" height="264" opacity="0.16" />

        {/* Folio top */}
        <text x="44" y="50" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.7" stroke="none">
          MAPA · VALLE DE SAN JUAN
        </text>
        <text x="856" y="50" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.6" stroke="none" textAnchor="end">
          18°50′ N · 71°15′ W
        </text>

        {/* Graticule — faint */}
        <g opacity="0.18">
          {[100, 160, 220, 280].map((y) => (
            <path key={`h-${y}`} d={`M44 ${y} L 856 ${y}`} />
          ))}
          {[140, 280, 420, 560, 700].map((x) => (
            <path key={`v-${x}`} d={`M${x} 68 L ${x} 288`} />
          ))}
        </g>

        {/* Mountain ridge — Cordillera Central */}
        <g opacity="0.9">
          <path d="M60 220 L 110 168 L 150 188 L 195 134 L 240 178 L 282 142 L 330 192 L 372 154 L 420 200 L 466 168 L 520 210 L 560 184 L 612 222 L 660 196 L 712 232 L 760 212 L 820 240 L 860 222" />
          <path d="M150 188 L 175 168" opacity="0.5" />
          <path d="M240 178 L 268 160" opacity="0.5" />
          <path d="M330 192 L 358 168" opacity="0.5" />
          <path d="M420 200 L 448 184" opacity="0.5" />
        </g>

        {/* Tobacco-field hatching south of the ridge */}
        <g opacity="0.42">
          {Array.from({ length: 28 }).map((_, i) => {
            const y = 252 + (i % 4) * 6;
            const x1 = 90 + (i % 14) * 50;
            return <path key={i} d={`M${x1} ${y} l 22 0`} />;
          })}
        </g>

        {/* Town markers */}
        <g>
          {[
            { x: 240, y: 234, label: "SAN JUAN DE LA MAGUANA" },
            { x: 440, y: 248, label: "LAS MATAS DE FARFÁN" },
            { x: 680, y: 240, label: "ELÍAS PIÑA" },
          ].map((m) => (
            <g key={m.label}>
              <circle cx={m.x} cy={m.y} r="3" fill={TOBACCO} stroke="none" />
              <circle cx={m.x} cy={m.y} r="7" opacity="0.5" />
              <text
                x={m.x}
                y={m.y + 22}
                fontFamily="Space Mono, monospace"
                fontSize="9"
                letterSpacing="2.2"
                fill={TOBACCO}
                opacity="0.85"
                stroke="none"
                textAnchor="middle"
              >
                {m.label}
              </text>
            </g>
          ))}
        </g>

        {/* Compass rose — right */}
        <g transform="translate(800 168)" opacity="0.8">
          <g className="ms-anim-breathe-y">
          <circle r="36" />
          <circle r="28" opacity="0.5" />
          <path d="M0 -36 L 0 36" />
          <path d="M-36 0 L 36 0" />
          <path d="M-26 -26 L 26 26" opacity="0.5" />
          <path d="M-26 26 L 26 -26" opacity="0.5" />
          <text x="0" y="-44" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">N</text>
          <text x="48" y="3" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">E</text>
          <text x="0" y="54" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">S</text>
          <text x="-48" y="3" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} stroke="none" textAnchor="middle">W</text>
          </g>
        </g>

        {/* Bottom-left marginalia */}
        <text x="44" y="288" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="12" fill={TOBACCO} opacity="0.7" stroke="none">
          Sierra de Neiba al sur. Tabaco al pie de la montaña.
        </text>
      </g>
    </svg>
  );
}

/* ───────────────────────── SmokeRibbon ─────────────────────────────
 * A long horizontal engraved smoke curl drifting subtly. Used as the
 * interlude between Master Blender and The Final Cigar — a visual
 * exhalation between the blending table and the smoke.
 * ──────────────────────────────────────────────────────────────── */
export function SmokeRibbon({ className = "" }) {
  return (
    <svg
      viewBox="0 0 1200 200"
      fill="none"
      className={className}
      aria-hidden="true"
      data-testid="smoke-ribbon"
    >
      <g stroke={TOBACCO} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {/* Plate hairline */}
        <rect x="16" y="16" width="1168" height="168" opacity="0.22" />

        {/* Folio */}
        <text x="44" y="48" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.65" stroke="none">
          PLATE · FUMUS
        </text>
        <text x="1156" y="48" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.55" stroke="none" textAnchor="end">
          FOLIO · 028
        </text>

        {/* Cigar tip on the left */}
        <g transform="translate(96 110)">
          <path d="M0 -10 L 60 -10 L 76 -2 L 76 2 L 60 10 L 0 10 Z" opacity="0.85" />
          <path d="M0 -10 L 0 10" opacity="0.55" />
          <path d="M-2 -8 Q -10 0 -2 8" opacity="0.55" />
          {/* glowing ember */}
          <circle cx="76" cy="0" r="2.4" fill={TOBACCO} opacity="0.6" stroke="none" />
        </g>

        {/* Three drifting smoke ribbons — each on its own slow loop */}
        <g className="ms-anim-smoke" style={{ animationDuration: "28s" }} opacity="0.8">
          <path
            d="M178 108 C 240 80 300 132 360 100 S 460 60 520 94 S 620 140 680 104 S 800 70 880 98 S 980 130 1060 96 S 1150 70 1180 88"
            strokeWidth="1.2"
          />
        </g>
        <g className="ms-anim-smoke" style={{ animationDuration: "36s", animationDelay: "-8s" }} opacity="0.55">
          <path
            d="M178 90 C 240 64 300 110 360 82 S 460 46 520 76 S 620 118 680 86 S 800 56 880 80 S 980 110 1060 78 S 1150 56 1180 72"
            strokeWidth="0.9"
          />
        </g>
        <g className="ms-anim-smoke" style={{ animationDuration: "44s", animationDelay: "-14s" }} opacity="0.4">
          <path
            d="M178 126 C 240 100 300 150 360 120 S 460 86 520 112 S 620 156 680 124 S 800 92 880 118 S 980 148 1060 116 S 1150 92 1180 108"
            strokeWidth="0.7"
          />
        </g>

        {/* Italic marginalia */}
        <text x="44" y="184" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="12" fill={TOBACCO} opacity="0.7" stroke="none">
          The exhalation — three ribbons of patience returning to the air.
        </text>
      </g>
    </svg>
  );
}

/* ───────────────────────── WaxSealMark ─────────────────────────────
 * Wax-seal stamp + manuscript-edge ornament. Used between Final Cigar
 * and Archive — the moment the journey is sealed into the family ledger.
 * ──────────────────────────────────────────────────────────────── */
export function WaxSealMark({ className = "" }) {
  return (
    <svg
      viewBox="0 0 1100 260"
      fill="none"
      className={className}
      aria-hidden="true"
      data-testid="wax-seal-mark"
    >
      <g stroke={TOBACCO} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
        {/* Plate hairline */}
        <rect x="16" y="16" width="1068" height="228" opacity="0.22" />

        {/* Folio */}
        <text x="44" y="48" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.65" stroke="none">
          PLATE · SIGILLUM
        </text>
        <text x="1056" y="48" fontFamily="Space Mono, monospace" fontSize="9" letterSpacing="2.2" fill={TOBACCO} opacity="0.55" stroke="none" textAnchor="end">
          FOLIO · 034
        </text>

        {/* Left-side filigree */}
        <g transform="translate(140 138)" opacity="0.55">
          <path d="M0 0 L 240 0" />
          <path d="M0 -8 L 24 0 L 0 8 Z" opacity="0.85" />
          <path d="M60 -4 L 70 0 L 60 4" />
          <path d="M118 -6 L 128 0 L 118 6" />
        </g>

        {/* Centerpiece — wax seal with subtle breath */}
        <g transform="translate(550 138)">
          <g className="ms-anim-seal">
          {/* outer wax ring */}
          <circle r="58" />
          <circle r="58" opacity="0.45" strokeDasharray="3 5" />
          <circle r="46" opacity="0.75" />
          {/* inner monogram */}
          <text x="0" y="6" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="30" fill={TOBACCO} stroke="none" textAnchor="middle">
            ER
          </text>
          {/* date arc */}
          <path d="M-42 -16 A 50 50 0 0 1 42 -16" opacity="0.4" id="seal-arc" />
          <text fontFamily="Space Mono, monospace" fontSize="8" letterSpacing="4" fill={TOBACCO} opacity="0.7" stroke="none">
            <textPath href="#seal-arc" startOffset="50%" textAnchor="middle">
              EST · MCMXLVIII
            </textPath>
          </text>
          {/* 8 radial stamp ticks */}
          {Array.from({ length: 8 }).map((_, i) => {
            const angle = (i * Math.PI) / 4;
            const x1 = Math.cos(angle) * 62;
            const y1 = Math.sin(angle) * 62;
            const x2 = Math.cos(angle) * 70;
            const y2 = Math.sin(angle) * 70;
            return (
              <path
                key={i}
                d={`M${x1.toFixed(1)} ${y1.toFixed(1)} L ${x2.toFixed(1)} ${y2.toFixed(1)}`}
                opacity="0.55"
              />
            );
          })}
          </g>
        </g>

        {/* Right-side filigree (mirror) */}
        <g transform="translate(720 138)" opacity="0.55">
          <path d="M0 0 L 240 0" />
          <path d="M240 -8 L 216 0 L 240 8 Z" opacity="0.85" />
          <path d="M180 -4 L 170 0 L 180 4" />
          <path d="M122 -6 L 112 0 L 122 6" />
        </g>

        {/* Italic marginalia */}
        <text x="44" y="224" fontFamily="Cormorant Garamond, serif" fontStyle="italic" fontSize="12" fill={TOBACCO} opacity="0.7" stroke="none">
          What is signed, is kept. — Sealed into the family ledger.
        </text>
      </g>
    </svg>
  );
}
